import React, { useEffect, useState, useRef } from 'react'
import Form from 'react-bootstrap/Form'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import { getDatabase, ref, get } from 'firebase/database';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels'

function MonthToMonthGraph({
    selectedCompany,
    selectedYear,
    selectedDep,
    dateStringToDateTime,
    parseAdditionalInfo,
    parseDaysOff,
    isDateBetween,
    calculatePercentageWithBonus,
    calculateOverallAtendence,
    parsePresentAndTotal,
    validRollKeys
}) {

    const chartRef = useRef(null)

    const subTotalsFromParsedData = (parsedRoll, additionalInfo) => {
        let tempPresentEmployees = 0
        let tempTotalEmployees = 0
        let rollsPercentage = {}

        Object.entries(parsedRoll).map(([depName, rolls], index) => {
            let normalizedRoll = {}

            for (const [rollDate, roll] of Object.entries(rolls)) {

                if (Object.values(normalizedRoll).length == 0) {
                    Object.keys(roll).forEach((a) => {
                        if (a !== 'presentPeople' && a !== 'totalofPeople') {
                            normalizedRoll[a] = "FALTA"
                        }
                    })
                }

                for (const [employee, status] of Object.entries(roll)) {
                    let between = false

                    const employeeAdditionalInfo = additionalInfo[employee]

                    if (employeeAdditionalInfo) {
                        for (const addInfo of employeeAdditionalInfo) {
                            between = isDateBetween(addInfo.daysOffBeginning, rollDate, addInfo.daysOffEnding)
                        }
                    }

                    if (!between && !Object.keys(normalizedRoll).includes(employee)) {
                        normalizedRoll[employee] = status
                    }

                    if (!between && (!Object.keys(normalizedRoll).includes(employee) || status === "PRESENTE")) {
                        normalizedRoll[employee] = status
                    }
                }
            }

            if (Object.keys(normalizedRoll).includes('presentPeople')) {
                tempPresentEmployees += normalizedRoll['presentPeople']
                tempTotalEmployees += normalizedRoll['totalofPeople']

                rollsPercentage[depName] = calculatePercentageWithBonus(tempPresentEmployees, tempTotalEmployees)
            } else {
                const statusList = Object.values(normalizedRoll)

                const present = statusList.filter(status => status === 'PRESENTE' || status === true).length

                rollsPercentage[depName] = calculatePercentageWithBonus(present, statusList.length)

                tempPresentEmployees += present
                tempTotalEmployees += statusList.length
            }
        })

        return Number(calculateOverallAtendence(rollsPercentage))
    }

    function getMonthName(monthNumber) {
        const months = [
            "Janeiro", "Fevereiro", "Março", "Abril",
            "Maio", "Junho", "Julho", "Agosto",
            "Setembro", "Outubro", "Novembro", "Dezembro"
        ];

        if (monthNumber < 1 || monthNumber > 12) return null;

        return months[monthNumber - 1];
    }

    const [start, setStart] = useState(1);
    const [end, setEnd] = useState(new Date().getMonth() + 1);
    const [allMonthPercentage, setAllMonthPercentage] = useState({}) // Store all data
    const [filteredMonthPercentage, setFilteredMonthPercentage] = useState({}) // Filtered data for display

    // Filter data based on start and end months
    const filterDataByMonths = (data, startMonth, endMonth) => {
        const filtered = {}
        const monthNames = Object.keys(data)

        // Get month numbers for filtering
        const startIndex = parseInt(startMonth) - 1 // Convert to 0-based index
        const endIndex = parseInt(endMonth) - 1

        for (let i = startIndex; i <= endIndex; i++) {
            const monthName = getMonthName(i + 1)
            if (data[monthName] !== undefined) {
                filtered[monthName] = data[monthName]
            }
        }

        return filtered
    }

    const updateGraphData = () => {
        const filteredData = filterDataByMonths(allMonthPercentage, start, end)
        setFilteredMonthPercentage(filteredData)
    }

    // Update filtered data whenever start, end, or allMonthPercentage changes
    useEffect(() => {
        updateGraphData()
    }, [start, end, allMonthPercentage])

    function MonthSelects() {

        const options = Array.from({ length: 12 }, (_, i) => i + 1);

        function updateStart(newValue) {
            const numValue = parseInt(newValue)
            setStart(numValue)
            if (numValue > end) {
                setEnd(numValue)
            }
        }

        function updateEnd(newValue) {
            const numValue = parseInt(newValue)
            setEnd(numValue)
            if (numValue < start) {
                setStart(numValue)
            }
        }

        return (
            <Form className='mb-3'>
                <Row>
                    <Col>
                        <Form.Label>Início</Form.Label>
                        <Form.Select value={start} onChange={(e) => updateStart(e.target.value)}>
                            {options.map((num) => (
                                <option key={num} value={num}>
                                    {getMonthName(num)}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col>
                        <Form.Label>Fim</Form.Label>
                        <Form.Select value={end} onChange={(e) => updateEnd(e.target.value)}>
                            {options.map((num) => (
                                <option key={num} value={num}>
                                    {getMonthName(num)}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>
            </Form>
        );
    }

    const parseDbData = (allRolls) => {
        let employeesAdditionalInfo = {}
        let parsedRolls = {}

        if (selectedDep) {
            allRolls = { [selectedDep]: { ...allRolls } }
        }

        for (const [depName, rolls] of Object.entries(allRolls)) {

            parsedRolls[depName] = {}

            let allDates = [] // [1, 3, 5, 6]
            let allRollDates = [] // [Date(2025, 11, 2), Date(2025, 11, 3), Date(2025, 11, 5)]
            let updatedRolls = {} // {Date(): rollInfo}

            for (let [rollDate, rollInfo] of Object.entries(rolls)) {
                rollDate = dateStringToDateTime(rollDate)

                if (!allDates.includes(rollDate.getDate())) {
                    allDates.push(rollDate.getDate())
                }

                allRollDates.push(rollDate)

                if (validRollKeys(Object.keys(rollInfo))) {
                    updatedRolls[rollDate] = rollInfo
                }
            }

            allDates.sort((a, b) => a - b)
            allRollDates.sort((a, b) => new Date(a) - new Date(b))

            let originalTime_registeredDatetime = {}

            // Buscando somente a ultima chamda registrada para cada originalTime
            for (const [registered, roll] of Object.entries(updatedRolls)) {
                const originalTime = dateStringToDateTime(roll.originalTime)
                originalTime_registeredDatetime[originalTime] = registered
            }

            Object.values(originalTime_registeredDatetime).forEach((rollDate) => {
                let originalTime = updatedRolls[rollDate]['originalTime']
                originalTime = dateStringToDateTime(originalTime)

                let uOriginalTime = new Date(originalTime.getTime())
                uOriginalTime.setHours(10, 0, 0, 0)

                if (!Object.keys(parsedRolls[depName]).some(d => new Date(d).toISOString() === uOriginalTime.toISOString())) {
                    parsedRolls[depName][uOriginalTime] = {}
                }

                const rollDateKeys = Object.keys(updatedRolls[rollDate])

                if (rollDateKeys.includes('employees')) {
                    let empList = updatedRolls[rollDate]['employees']

                    const countAttendance = updatedRolls[rollDate]['countAttendance'] ?? true

                    empList.forEach((e) => {
                        const employeeName = e.employee.toString().trim().replaceAll('  ', ' ')

                        if (!countAttendance) {
                            parsedRolls[depName][uOriginalTime][employeeName] = 'FALTA'
                        } else if (Object.keys(parsedRolls[depName][uOriginalTime]).includes(employeeName)) {
                            if (e.status === 'PRESENTE') {
                                parsedRolls[depName][uOriginalTime][employeeName] = e.status
                            }
                        } else {
                            parsedRolls[depName][uOriginalTime][employeeName] = e.status
                        }

                        const employeeKeys = Object.keys(e)

                        if (employeeKeys.includes("additionalInfo") &&
                            employeeKeys.includes("daysOffBeginning") &&
                            employeeKeys.includes("daysOffEnding")) {
                            const [additionalInfo, additionalInfoIconPath, additionalInfoShort] = parseAdditionalInfo(e["additionalInfo"])

                            if (!Object.keys(employeesAdditionalInfo).includes(employeeName)) {
                                employeesAdditionalInfo[employeeName] = []
                            }

                            employeesAdditionalInfo[employeeName].push({
                                'additionalInfo': additionalInfo,
                                'additionalInfoIconPath': additionalInfoIconPath,
                                'daysOffBeginning': parseDaysOff(e['daysOffBeginning']),
                                'daysOffEnding': parseDaysOff(e['daysOffEnding']),
                                'additionalInfoShort': additionalInfoShort,
                                'registeredDateTime': rollDate
                            })
                        }
                    })

                } else if (rollDateKeys.includes('totalofPeople')) {

                    const [present, total] = parsePresentAndTotal(updatedRolls[rollDate]['presentPeople'], updatedRolls[rollDate]['totalofPeople'])

                    if (Object.keys(parsedRolls[depName][uOriginalTime]).includes('totalofPeople')) {
                        parsedRolls[depName][uOriginalTime]['presentPeople'] += present
                        parsedRolls[depName][uOriginalTime]['totalofPeople'] += total
                    } else {
                        parsedRolls[depName][uOriginalTime]['presentPeople'] = present
                        parsedRolls[depName][uOriginalTime]['totalofPeople'] = total
                    }
                }
            })
        }

        return [parsedRolls, employeesAdditionalInfo]
    }

    const getMonthToMonthData = async (db) => {
        const currentMonth = new Date().getMonth() + 1;

        let monthPercentage = {};

        const promises = [];
        for (let month = 0; month < currentMonth; month++) {
            let path = `/rolls_remodel/${selectedYear}/${month + 1}/${selectedCompany}`;
            if (selectedDep) {
                path += `/${selectedDep}`
            }
            const dbRef = ref(db, path);

            const p = get(dbRef).then((snapshot) => {
                if (snapshot.exists()) {
                    const [parsedRoll, additionalInfo] = parseDbData(snapshot.val());
                    const subtotal = subTotalsFromParsedData(parsedRoll, additionalInfo);
                    monthPercentage[getMonthName(month + 1)] = Number(subtotal).toFixed(0);
                }
            });

            promises.push(p);
        }

        await Promise.all(promises);
        setAllMonthPercentage(monthPercentage); // Store all data
    };

    function getHighest(data) {
        let highest = 0

        data.forEach((value) => {
            value = parseInt(value)
            if (value > highest) {
                highest = value
            }
        })

        return parseInt(highest) + 14
    }

    useEffect(() => {
        const db = getDatabase()
        getMonthToMonthData(db)
    }, [selectedDep]);

    ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels)

    // Use filtered data for the chart
    const labels = Object.keys(filteredMonthPercentage)
    const values = Object.values(filteredMonthPercentage)

    const data = {
        labels,
        datasets: [
            {
                label: "% Presença mês",
                data: values,
                backgroundColor: '#255683'
            }
        ]
    }

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false
            },
            datalabels: {
                color: '#000',
                clip: true,
                align: 'top',
                anchor: 'end',
                clamp: true,
                font: {
                    size: 16
                },
                formatter: function (value, context) {
                    return `${value}%`
                }
            }
        },
        layout: {
            padding: 0
        },
        scales: {
            y: {
                beginAtZero: true,
                max: values.length > 0 ? getHighest(values) : 100,
                ticks: {
                    display: false   // hides the numbers
                },
                grid: {
                    drawTicks: false // optional, hides tick marks
                }
            },
            x: {
                ticks: {
                    autoSkip: false
                }
            }
        }
    }

    return <Col md={6}>
        <h5>Aderência ao programa de ginástica laboral - Anual</h5>
        <Bar data={data} options={options} ref={chartRef} />
        <MonthSelects />
    </Col>
}

export default MonthToMonthGraph